import { alpha, Box, Typography, useTheme } from "@mui/material";
import Icon from "../../Icon/Icon";

// Maps a semantic `tone` to a { accent, soft } pair from the MUI theme palette.
// `accent` is the strong color (icon + delta), `soft` the tinted icon background.
const TONE_MAP = {
  blue: { accent: "blue", soft: "blueV" },
  yellow: { accent: "yellow", soft: "yellowV" },
  green: { accent: "green", soft: "greenV" },
  red: { accent: "red", soft: "redV" },
};

/**
 * IndicatorCard — a single KPI card: icon, label and a big number, with an
 * optional delta (vs. previous period). `deltaTitle` explains what the delta
 * compares against (native tooltip on the arrow).
 *
 * Color comes either from a semantic `tone` (theme palette family) or, when the
 * caller already has an explicit color (e.g. a status color), from `accentColor`
 * — in which case the soft background is derived from it.
 */
export const IndicatorCard = ({
  label,
  value,
  delta,
  deltaTitle,
  iconName,
  tone = "blue",
  accentColor,
  onClick,
}) => {
  const theme = useTheme();
  const { accent, soft } = TONE_MAP[tone] || TONE_MAP.blue;
  const accentColorResolved = accentColor || theme.palette[accent]?.main;
  const softColor = accentColor
    ? alpha(accentColor, 0.14)
    : theme.palette[soft]?.main;

  const hasDelta = typeof delta === "number" && delta !== 0;
  const deltaUp = delta > 0;
  const clickable = typeof onClick === "function";

  return (
    <Box
      className="dyTheme1 dyBorder1 rounded"
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      sx={{
        p: 2.5,
        height: "100%",
        display: "flex",
        alignItems: "center",
        gap: 2,
        ...(clickable && {
          cursor: "pointer",
          transition: "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
            borderColor: accentColorResolved,
          },
          "&:focus-visible": {
            outline: `2px solid ${accentColorResolved}`,
            outlineOffset: 2,
          },
        }),
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: 2,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: softColor,
        }}
      >
        <Icon name={iconName} size={28} color={accentColorResolved} />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" noWrap>
          {label}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
            {value}
          </Typography>
          {hasDelta && (
            <Typography
              variant="caption"
              title={deltaTitle}
              sx={{
                fontWeight: 600,
                color: deltaUp ? theme.palette.green?.main : theme.palette.red?.main,
                ...(deltaTitle && { cursor: "help" }),
              }}
            >
              {deltaUp ? "▲" : "▼"} {Math.abs(delta)}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default IndicatorCard;
